<template>
  <Teleport to="body">
    <Transition
      enter-active-class="modal-enter-active"
      enter-from-class="modal-enter-from"
      enter-to-class="modal-enter-to"
      leave-active-class="modal-leave-active"
      leave-from-class="modal-leave-from"
      leave-to-class="modal-leave-to"
    >
      <div
        v-if="isOpen"
        class="modal-overlay"
        :class="overlayClasses"
        :style="overlayInlineStyle"
        :id="exportModalId || undefined"
        :data-vp-export-modal-for="exportAnchorId || undefined"
        :data-vp-modal="exportModalId ? '' : undefined"
        :data-static-modal="exportModalId ? '' : undefined"
        data-vp-modal-backdrop
        data-static-modal-backdrop
        @click="handleOverlayClick"
        tabindex="-1"
        role="dialog"
        :aria-modal="true"
        :aria-label="props['aria-label']"
        :aria-describedby="props['aria-describedby']"
      >
        <!-- Modal Container -->
        <div
          ref="modalContainer"
          class="modal-container"
          :class="containerClasses"
          @click.stop
          role="document"
        >
          <!-- Modal Content -->
          <div class="modal-content" :class="contentClasses">
            <!-- Header -->
            <div v-if="showHeaderBar" class="modal-header">
              <div class="modal-header-content">
                <slot name="header">
                  <template v-if="useEnhancedDefaultHeader">
                    <div class="flex items-center gap-3 min-w-0 w-full">
                      <div v-if="$slots['header-leading']" class="shrink-0">
                        <slot name="header-leading" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <h2 v-if="(title || '').trim()" class="modal-title">{{ title }}</h2>
                        <p v-if="trimmedTitleSubtitle" class="modal-title-subtitle">{{ trimmedTitleSubtitle }}</p>
                      </div>
                    </div>
                  </template>
                  <h2 v-else-if="(title || '').trim()" class="modal-title">{{ title }}</h2>
                </slot>
              </div>
              <button
                v-if="showCloseButton"
                class="modal-close-button"
                data-vp-modal-close
                data-static-action="close-modal"
                @click="requestDismiss"
                aria-label="Close modal"
                type="button"
              >
                <BaseIcon size="small" variant="ghost">
                  <XIcon />
                </BaseIcon>
              </button>
            </div>

            <!-- Body -->
            <BaseScrollbar
              ref="modalBodyScrollbar"
              :horizontal="false"
              :vertical="true"
              :update-key="modalUpdateKey"
              :max-height="'calc(100vh - 10rem)'"
              :options="{
                wheelSpeed: 1,
                wheelPropagation: false,
                swipeEasing: true,
                suppressScrollX: true,
                suppressScrollY: false
              }"
              class="modal-body"
              :class="bodyClasses"
            >
              <slot>
                <!-- Default content slot -->
              </slot>
            </BaseScrollbar>

            <!-- Footer: pass requestClose so Cancel matches X/backdrop/Escape dirty guard -->
            <div v-if="slots.footer" class="modal-footer">
              <slot name="footer" :request-close="requestDismiss" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount, useSlots } from 'vue'
import { lockModalBodyScroll, unlockModalBodyScroll } from '@/utils/modalBodyScrollLock'
import { OVERLAY_Z_MODAL } from '@/lib/ui/overlayLayers'
import BaseIcon from '@/components/ui/BaseIcon.vue'
import XIcon from '../icons/XIcon.vue'
import BaseScrollbar from '../layout/BaseScrollbar.vue'
import type { ComponentPublicInstance } from 'vue'
import { isTopmostModal, pushOpenModal, removeOpenModal } from '@/composables/modalStack'

interface Props {
  modelValue?: boolean
  title?: string
  size?: 'small' | 'medium' | 'large' | 'xl' | 'fullscreen'
  closeOnBackdrop?: boolean
  closeOnEscape?: boolean
  showCloseButton?: boolean
  persistent?: boolean
  /** Optional extra class(es) for the modal container (e.g. min-h-[50vh]). Applied only to this instance. */
  containerClass?: string
  /** Optional class(es) for the teleported overlay root (e.g. sd-pages sd-modal-overlay for sd-pages theming). */
  overlayClass?: string
  'aria-label'?: string
  'aria-describedby'?: string
  /** Muted line under title (default header only). Ignored when empty. */
  titleSubtitle?: string
  /**
   * When set, invoked instead of closing immediately for X, backdrop, Escape, and footer `requestClose`.
   * Call `complete()` to finish closing (after optional confirm).
   */
  beforeDismiss?: (complete: () => void) => void
  /** Stacking order; pick a tier from src/lib/ui/overlayLayers.ts rather than a raw number. */
  overlayZIndex?: number
  /** Visual Parity static export: clone this overlay for captureTargetId. */
  exportAnchorId?: string
  /** Stable id for static modal open/close (data-vp-modal-open). */
  exportModalId?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  closeOnBackdrop: true,
  closeOnEscape: true,
  showCloseButton: true,
  persistent: false,
  overlayZIndex: OVERLAY_Z_MODAL
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'modal-open': []
  'modal-close': []
  'backdrop-click': [event: MouseEvent]
}>()

// Get slots
const slots = useSlots()

const trimmedTitleSubtitle = computed(() => (props.titleSubtitle ?? '').trim())

const useEnhancedDefaultHeader = computed(
  () => trimmedTitleSubtitle.value.length > 0 || !!slots['header-leading']
)

const showHeaderBar = computed(() => {
  if (slots.header) return true
  if (props.showCloseButton) return true
  if ((props.title ?? '').trim()) return true
  if (trimmedTitleSubtitle.value.length > 0) return true
  if (slots['header-leading']) return true
  return false
})

// State
const isOpen = computed({
  get: () => props.modelValue || false,
  set: (value) => emit('update:modelValue', value)
})

const modalContainer = ref<HTMLElement>()
const modalBodyScrollbar = ref<ComponentPublicInstance & { forceUpdate?: () => Promise<void> } | null>(null)
const previousActiveElement = ref<HTMLElement>()
const modalUpdateKey = ref(0)

// Computed classes
const overlayClasses = computed(() => [
  'modal-overlay',
  {
    'modal-overlay--blur': true,
    'modal-overlay--persistent': props.persistent
  },
  ...(props.overlayClass ?? '')
    .split(/\s+/)
    .filter(Boolean),
])

const containerClasses = computed(() => [
  `modal-container--${props.size}`,
  ...(props.containerClass ? [props.containerClass] : []),
])

const contentClasses = computed(() => [
  'modal-content',
  {
    'modal-content--no-header': !showHeaderBar.value,
    'modal-content--no-footer': !slots.footer
  }
])

const bodyClasses = computed(() => [
  'modal-body',
  {
    'modal-body--scrollable': true
  }
])

const overlayInlineStyle = computed(() => ({
  zIndex: props.overlayZIndex
}))

// Methods
const openModal = async () => {
  enterStack()
  // Store the currently focused element
  previousActiveElement.value = document.activeElement as HTMLElement
  
  lockModalBodyScroll()
  
  emit('modal-open')
  
  await nextTick()
  
  // Focus the modal container
  if (modalContainer.value) {
    modalContainer.value.focus()
  }
  
  // Trigger scrollbar update after modal is fully rendered and visible
  // CRITICAL: BaseScrollbar needs container to have computed height before it can detect overflow
  const updateScrollbar = async () => {
    await nextTick()
    // Force a reflow to ensure height is calculated
    if (modalContainer.value) {
      modalContainer.value.offsetHeight // Force reflow
    }
    if (modalBodyScrollbar.value?.forceUpdate) {
      await modalBodyScrollbar.value.forceUpdate()
    }
    modalUpdateKey.value++
  }
  
  // Immediate update after DOM is ready
  await nextTick()
  requestAnimationFrame(async () => {
    await updateScrollbar()
  })
  
  // Update after transition completes (modal is fully visible)
  setTimeout(async () => {
    requestAnimationFrame(async () => {
      requestAnimationFrame(async () => {
        await updateScrollbar()
        // Additional updates to catch late layout changes
        setTimeout(async () => {
          requestAnimationFrame(async () => {
            await updateScrollbar()
            setTimeout(async () => {
              requestAnimationFrame(async () => {
                await updateScrollbar()
              })
            }, 100)
          })
        }, 200)
      })
    })
  }, 450) // Wait for transition animation to complete
}

function finalizeClose() {
  if (props.persistent) return

  isOpen.value = false
  emit('modal-close')

  unlockModalBodyScroll()

  if (previousActiveElement.value) {
    previousActiveElement.value.focus()
  }
}

/** User-initiated close (X, backdrop, Escape, footer requestClose). Runs optional beforeDismiss guard. */
function requestDismiss() {
  if (props.persistent) return

  if (props.beforeDismiss) {
    props.beforeDismiss(finalizeClose)
  } else {
    finalizeClose()
  }
}

const handleOverlayClick = (event: MouseEvent) => {
  if (props.closeOnBackdrop && !props.persistent) {
    emit('backdrop-click', event)
    requestDismiss()
  }
}

const modalId = Symbol('modal')
const enterStack = () => pushOpenModal(modalId)
const leaveStack = () => removeOpenModal(modalId)

/** Escape is handled at document level so it works wherever focus sits, but only the topmost modal closes. */
const onDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || !isOpen.value) return
  // An inner widget (dropdown, nested modal) already consumed this press.
  if (event.defaultPrevented) return
  if (!isTopmostModal(modalId)) return
  if (!props.closeOnEscape || props.persistent) return
  event.preventDefault()
  requestDismiss()
}

defineExpose({
  requestClose: requestDismiss
})

// Focus management
const trapFocus = (event: KeyboardEvent) => {
  if (event.key !== 'Tab') return
  
  const focusableElements = modalContainer.value?.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )
  
  if (!focusableElements?.length) return
  
  const firstElement = focusableElements[0] as HTMLElement
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement
  
  if (event.shiftKey) {
    if (document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    }
  } else {
    if (document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }
}

// Watch for open/close changes
watch(isOpen, async (newValue) => {
  if (newValue) {
    await openModal()
  } else {
    leaveStack()
    unlockModalBodyScroll()
  }
})

// Event listeners
onMounted(() => {
  document.addEventListener('keydown', onDocumentKeydown)
  if (isOpen.value) {
    openModal()
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onDocumentKeydown)
  leaveStack()
  if (isOpen.value) {
    unlockModalBodyScroll()
  }
})
</script>

<style scoped>
/* Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: var(--color-modal-overlay);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.modal-overlay--blur {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.modal-overlay--persistent {
  pointer-events: none;
}

.modal-overlay--persistent .modal-container {
  pointer-events: auto;
}

/* Modal Container */
.modal-container {
  position: relative;
  width: 100%;
  max-width: 100%;
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  background-color: var(--color-modal-background);
  border-radius: 0.75rem;
  box-shadow: var(--color-modal-shadow);
  border: 1px solid var(--color-modal-border);
  outline: none;
  overflow: hidden;
}

/* Size variants */
.modal-container--small {
  max-width: 24rem; /* 384px */
}

.modal-container--medium {
  max-width: 32rem; /* 512px */
}

.modal-container--large {
  max-width: 48rem; /* 768px */
}

.modal-container--xl {
  max-width: min(1200px, calc(100vw - 2rem)); /* ~1200px with safe margins */
  max-height: calc(100vh - 2rem);
  width: min(1200px, calc(100vw - 2rem));
}

.modal-container--fullscreen {
  max-width: 100vw;
  max-height: 100vh;
  width: 100vw;
  height: 100vh;
  border-radius: 0;
  margin: 0;
}

/* Modal Content */
.modal-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden; /* Ensure content doesn't overflow container */
}

.modal-content--no-header .modal-body {
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
}

.modal-content--no-footer .modal-body {
  border-bottom-left-radius: 0.75rem;
  border-bottom-right-radius: 0.75rem;
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1.5rem 1.5rem;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.modal-header-content {
  flex: 1;
  min-width: 0;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
  line-height: 1.5;
}

.modal-title-subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--color-text-muted);
}

.modal-close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  margin-left: 1rem;
  background-color: transparent;
  border: none;
  border-radius: 0.375rem;
  color: var(--color-text-muted);
  cursor: pointer;
  
  flex-shrink: 0;
}

.modal-close-button:hover {
  background-color: var(--color-surface-muted);
  color: var(--color-text);
}

.modal-close-button:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* Body - CRITICAL: Must have constrained height for BaseScrollbar to work */
.modal-body {
  flex: 1 1 0%;
  padding: 0; /* Padding moved to BaseScrollbar content */
  /* overflow handled by BaseScrollbar */
  color: var(--color-text);
  min-height: 0;
  overflow: hidden; /* Prevent native scrolling - BaseScrollbar handles it */
  display: flex;
  flex-direction: column;
  position: relative; /* Ensure proper positioning for scrollbar */
  /* Matches scroll-el horizontal padding so rail reserve does not collapse the right inset */
  --base-scrollbar-inline-end-padding: 1.5rem;
}

/* Style only the modal body's own scrollbar (direct children), not nested BaseScrollbars (e.g. dropdowns) */
.modal-body > :deep(.base-scrollbar-scroll-el) {
  padding: 1.5rem;
  box-sizing: border-box;
  height: 100% !important;
  max-height: 100% !important;
  position: relative !important;
  overflow: auto !important;
  display: block !important;
}

.modal-body > :deep(.base-scrollbar-rail-y),
.modal-body > :deep(.base-scrollbar-thumb-y) {
  opacity: 1;
}

.modal-body--scrollable {
  /* overflow handled by BaseScrollbar */
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(-10px);
}

.modal-enter-to .modal-container,
.modal-leave-from .modal-container {
  transform: scale(1) translateY(0);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .modal-overlay {
    padding: 0;
  }
  
  .modal-container {
    border-radius: 0;
    max-height: 100vh;
    height: 100vh;
  }
  
  .modal-container--small,
  .modal-container--medium,
  .modal-container--large,
  .modal-container--xl {
    max-width: 100vw;
    width: 100vw;
  }
  
  .modal-header {
    padding: 1rem 1rem 0 1rem;
  }
  
  .modal-body {
    padding: 0;
    max-height: calc(100vh - 8rem); /* Smaller header/footer on mobile */
    --base-scrollbar-inline-end-padding: 1rem;
  }
  
  .modal-body > :deep(.base-scrollbar-scroll-el) {
    padding: 1rem;
  }
  
  .modal-footer {
    padding: 1rem;
  }
}

/* Focus styles */
.modal-container:focus {
  outline: none;
}

/* Scrollbar styling - now handled by Perfect Scrollbar */
/* Custom scrollbar styling can be added via Perfect Scrollbar CSS classes if needed */
</style>
